<template>
  <div class="w-full space-y-3">
    <div class="w-full">
      <SelectSearch
        v-bind="$attrs"
        v-model:search="search"
        :clear-event="handleClearEvent"
        :clear="clear"
        :size="size"
        :options="{
          name: $attrs.id,
          urlSearch: 'personaService',
          action: 'searchPaciente',
          params: { limit: searchLimit },
          mapOptions: (item: IConfPersonasPaciente) => {
            return {
              optionText: `${item.copeFullName}`,
              optionSubText: `${item.tipoidentificacion?.comaNombrecorto}-${item.copeIdentificacion}`,
              optionValue: item.copeId
            }
          }
        }"
        :tabs="true"
        :group-field="{ mapOptions: (item: IConfPersonasPaciente) => `${item.tipoidentificacion?.comaNombrecorto}` }"
        :is-group="true"
        @select-raw="selectPaciente($event)"
        @update:search="(val: string) => checkEmptyValue()"
      />
    </div>
    <template v-if="isAlter">
      <div class="flex justify-end">
        <template v-if="buttonsStyle != 'button'">
          <div
            class="flex items-center text-blue-600 hover:text-blue-500 opacity-90 cursor-pointer"
          >
            <a
              v-if="_Paciente == null"
              class="rounded-md px-3 py-1 bg-blue-50 hover:bg-blue-100"
              @click="handleOpenPacienteModal"
              >Agregar paciente</a
            >
            <a
              v-else
              class="rounded-md px-3 py-1 bg-blue-50 hover:bg-blue-100"
              @click="handleOpenPacienteModal"
              >Editar paciente</a
            >
          </div>
        </template>
        <template v-else>
          <ButtonDefault
            class="flex items-center rounded-md"
            append="right"
            :title="_Paciente != null ? 'Editar paciente' : 'Agregar paciente'"
            @click="handleOpenPacienteModal"
          >
            <PencilSquareIcon
              v-if="_Paciente != null"
              class="shrink-0 size-5"
            />
            <UserPlusIcon v-else class="shrink-0 size-5" />
          </ButtonDefault>
        </template>
      </div>
    </template>
  </div>

  <div v-if="alertBd && _Paciente" class="min-w-full mt-4">
    <alert-default
      v-if="alertMessages.length === 1 && alertMessages[0] === 'El paciente no se encuentra activo en las base de datos de las EPS.'"
      :texto="alertMessages.join('\n')"
      titulo="Información importante"
      :color="alertColor"
      icono="ExclamationTriangleIcon"
    >
    </alert-default>
    <div
      v-else-if="Object.keys(alertGroups).length > 0"
      class="flex w-full max-w-[640px] rounded-xl bg-blue-100 p-4 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-500"
      role="alert"
    >
      <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />

      <div class="ml-3">
        <h3 class="font-semibold">Información importante</h3>
        <p class="my-1">El paciente se encuentra en las bases de datos activas con los siguientes contratos:</p>

        <div v-for="(contratos, periodo) in alertGroups" :key="periodo" class="mt-3">
          <p class="font-medium">Base de datos de {{ periodo }}:</p>
          <ul class="mt-1 ml-4 list-disc list-inside space-y-1">
            <li v-for="(c, i) in contratos" :key="i" class="font-medium">{{ c }}</li>
          </ul>
        </div>

      </div>
    </div>
    <DetailsPatient
      v-if="details && _Paciente"
      :paciente="_Paciente"
      class="mt-3"
    />
  </div>
  <div class="min-w-full mt-4">
    <teleport to="body">
      <ModalDefault
        :id="modalUniqueId"
        title="Paciente"
        size="lg"
        :button="false"
      >
        <template #modal-body>
          <FormPacienteNuevo
            v-if="showModalForm"
            :modal-id="modalUniqueId"
            :is-modal="true"
            :cope-id="_Paciente ? _Paciente.copeId : undefined"
            @nuevo-paciente="handleNuevoPaciente"
          />
        </template>
      </ModalDefault>
    </teleport>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, nextTick, watch, computed, getCurrentInstance } from 'vue'
import { UserPlusIcon, PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { openModal } from '@/modules/Core/usings/generales'
import type {
  IBDPaciente,
  IConfPersonasPaciente
} from '@/modules/Administracion/interfaces'
import type { ICitaAgendamiento } from '@/modules/Agendamiento/interfaces'
import {
  agendamientoService,
  admisionService,
  contratoService,
  personaService
} from '@/services'
import FormPacienteNuevo from '@/modules/Administracion/views/Pacientes/PacientesForm.vue'
import type {
  ICitaAdmision,
  tipoGeneralesAdmisiones
} from '@/modules/Admision/interfaces'
const props = defineProps({
  onBeforeChange: {
    type: Function,
    default: null
  },
  clear: {
    type: Boolean,
    default: false
  },
  isAlter: {
    type: Boolean,
    default: true
  },
  alertBd: {
    type: Boolean,
    default: false
  },
  details: {
    type: Boolean,
    default: true
  },
  clearEvent: {
    type: Function,
    default: null
  },
  size: {
    type: String,
    default: 'default',
    required: false
  },
  buttonsStyle: {
    type: String,
    default: 'default',
    required: false
  },
  paciente: {
    type: Object as () => IConfPersonasPaciente,
    required: false,
    default: null
  },
  lastContract: {
    type: Boolean,
    default: false
  },
  citas: {
    type: Boolean,
    default: false
  },
  generalAdmision: {
    type: Boolean,
    default: false
  },
  modalId: {
    type: String,
    default: undefined
  },
  searchLimit: {
    type: Number,
    default: undefined
  }
})
const {
  onBeforeChange,
  isAlter = true,
  alertBd,
  lastContract,
  citas,
  generalAdmision,
  searchLimit
} = props
const search = defineModel<string>('search')
const _Paciente = ref<IConfPersonasPaciente | null>(null)
const alertMessages = ref<string[]>([])
const alertColor = ref('yellow')
const alertGroups = ref<Record<string, string[]>>({})
const showFormPaciente = ref(true)
const showModalForm = ref(false)
const instance = getCurrentInstance()
const modalUniqueId = computed(
  () => props.modalId ?? `modal_paciente_${instance?.uid ?? 'default'}`
)

const selectPaciente = (paciente: IConfPersonasPaciente) => {
  onChange({ optionValue: paciente.copeId as number, raw: paciente })
}

watch(
  () => props.paciente,
  newPaciente => {
    if (newPaciente) {
      _Paciente.value = newPaciente
      if (search.value !== newPaciente.copeFullName) {
        search.value = newPaciente.copeFullName || ''
      }
    } else {
      _Paciente.value = null
    }
  },
  { immediate: true, deep: true }
)

/**
 * Maneja el proceso cuando se crea o actualiza un paciente
 * @param {IConfPersonasPaciente} pacienteActualizado - Datos del paciente que fue actualizado o creado
 * @returns {Promise<void>}
 */
const handleNuevoPaciente = async (
  pacienteActualizado: IConfPersonasPaciente
) => {
  const pacienteCopy = JSON.parse(JSON.stringify(pacienteActualizado))
  _Paciente.value = null
  showFormPaciente.value = true
  alertMessages.value = []
  alertGroups.value = {}
  search.value = pacienteCopy.copeFullName || ''
  await nextTick()
  onChange({ optionValue: pacienteCopy.copeId, edited: true, raw: pacienteCopy })
}

/**
 * Procesa el cambio de paciente seleccionado
 * @param {{ optionValue: number, raw?: IConfPersonasPaciente }} param0 - ID y datos opcionales del paciente seleccionados
 * @returns {Promise<void>}
 */
const onChange = async ({
  optionValue,
  edited,
  raw
}: {
  optionValue: number
  edited?: boolean
  raw?: IConfPersonasPaciente
}) => {
  if (onBeforeChange) {
    await onBeforeChange(onTriggerChange, optionValue, { edited, raw })
  } else {
    await onTriggerChange(optionValue, raw)
  }
}

/**
 * Verifica si el campo de búsqueda está vacío y reinicia el estado interno
 */
function checkEmptyValue() {
  if (search.value === '' || search.value === null) {
    if (_Paciente.value !== null) {
      _Paciente.value = null
      showFormPaciente.value = true
      alertMessages.value = []
      alertGroups.value = {}
      showModalForm.value = false
    }
  }
}

/**
 * Maneja el evento de limpieza desde el componente SelectSearch
 */
const handleClearEvent = () => {
  search.value = ''
  if (props.clearEvent) {
    props.clearEvent()
  }
  checkEmptyValue()
}

/**
 * Abre el modal para crear o editar un paciente
 * @returns {Promise<void>}
 */
const handleOpenPacienteModal = async () => {
  showModalForm.value = false
  await nextTick()
  openModal(modalUniqueId.value)
  requestAnimationFrame(async () => {
    await nextTick()
    showModalForm.value = true
  })
}

/**
 * Interfaz para la respuesta del disparador de cambio de paciente
 */
interface IResponseTrigger {
  coppId: number
  paciente: IConfPersonasPaciente | null
  datos?: ICitaAdmision
  citas_programadas?: ICitaAgendamiento[]
  citas_asistidas?: ICitaAgendamiento[]
  bd?: IBDPaciente[]
  generalesAdmiciones?: tipoGeneralesAdmisiones
}

/**
 * Obtiene datos basados en el ID del paciente seleccionado
 * @param {number} optionValue - ID del paciente seleccionado
 * @param {IConfPersonasPaciente} [raw] - Datos del paciente ya cargados
 * @returns {Promise<IResponseTrigger | undefined>} Datos del paciente y otra información relacionada
 */
const onTriggerChange = async (
  optionValue: number,
  raw?: IConfPersonasPaciente
): Promise<IResponseTrigger | undefined> => {
  window.notificacion.loading().circle()
  if (!optionValue) {
    _Paciente.value = null
    return undefined
  }
  showFormPaciente.value = false
  try {
    let paciente = raw

    if (!paciente) {
      if (props.paciente && props.paciente.copeId === optionValue) {
        paciente = props.paciente
      } else {
        // Si no tenemos los datos y es un cambio (ej. carga inicial por ID),
        // consultamos solo lo necesario del paciente
        const { data } = await personaService.viewPersona('pacientes', optionValue)
        paciente = data.body as IConfPersonasPaciente
      }
    }

    const coppId = optionValue

    if (paciente) {
      _Paciente.value = paciente
      if (search.value !== paciente.copeFullName) {
        search.value = paciente.copeFullName || ''
      }
    } else {
      _Paciente.value = null
    }

    const response: IResponseTrigger = {
      coppId,
      paciente: _Paciente.value
    }

    if (lastContract) {
      const datos = await admisionService.getUltimoContrato(optionValue)
      response.datos = datos as ICitaAdmision
    }

    if (citas) {
      const citasData = await getCitas(coppId)
      const hoy = new Date().getTime()
      const citas_programadas = citasData.filter(
        (cita: ICitaAgendamiento) => new Date(cita.ciagFecha).getTime() > hoy
      )
      const citas_asistidas = citasData.filter((cita: ICitaAgendamiento) =>
        ['FINALIZADO', 'ADMISIONADO'].some(
          e => e == cita.admision?.ciadTipoEstado
        )
      )
      response.citas_programadas = citas_programadas
      response.citas_asistidas = citas_asistidas
    }
    if (alertBd) {

      const { data: databd } = await contratoService.listByPaciente(_Paciente.value?.copeId as number)
      const bd = databd.body
      response.bd = bd
      alertMessages.value = []
      alertColor.value = 'yellow'
      if (bd != null && bd.length > 0) {
        const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

        const sortedBd = [...bd].sort((a, b) => {
          const yDelta = (parseInt(b.basedato?.cobdTipoAnio || '0') || 0) - (parseInt(a.basedato?.cobdTipoAnio || '0') || 0)
          if (yDelta !== 0) return yDelta
          return meses.indexOf(b.basedato?.cobdTipoMes?.toLowerCase() || '') - meses.indexOf(a.basedato?.cobdTipoMes?.toLowerCase() || '')
        })

        const grouped: Record<string, string[]> = {}
        sortedBd.forEach(b => {
          const key = `${b.basedato?.cobdTipoMes} ${b.basedato?.cobdTipoAnio}`
          if (!grouped[key]) grouped[key] = []
          const nombre = b.basedato?.contrato?.cocoNombre
          if (b.basedato?.contrato && nombre) grouped[key].push(nombre)
        })
        const filteredGrouped = Object.fromEntries(
          Object.entries(grouped).filter(([, value]) => value.length > 0)
        )

        if (Object.keys(filteredGrouped).length > 0) {
          alertGroups.value = filteredGrouped
          alertMessages.value = []
          alertColor.value = 'blue'
        } else {
          alertGroups.value = {}
          alertMessages.value.push(
            'El paciente no se encuentra activo en la base de datos de la EPS.'
          )
          alertColor.value = 'yellow'
        }
      } else {
        alertGroups.value = {}
        alertMessages.value.push(
          'El paciente no se encuentra activo en la base de datos de la EPS.'
        )
      }
    }
    if (generalAdmision) {
      const { data: getGeneralesAdmision } = await admisionService.getGenerales(
        {
          coppId: optionValue
        }
      )
      const generalesAdmiciones = getGeneralesAdmision.body
      response.generalesAdmiciones = generalesAdmiciones
    }

    showFormPaciente.value = true
    return response
  } catch (error) {
    console.error('Error al obtener datos del paciente:', error)
    _Paciente.value = null
    showFormPaciente.value = true
    return undefined
  }finally {
    await nextTick()
    window.notificacion.loading().remove()
  }
}

/**
 * Obtiene las citas del paciente
 * @param {number} coppId - ID del paciente
 * @returns {Promise<ICitaAgendamiento[]>} Lista de citas del paciente
 */
const getCitas = async (coppId: number) => {
  const { data } = await agendamientoService.search({
    offset: 0,
    limit: 10,
    coppId: coppId
  })
  return data.body.rows || []
}

onMounted(() => {
  if (props.paciente) {
    _Paciente.value = props.paciente
    if (search.value !== props.paciente.copeFullName) {
      search.value = props.paciente.copeFullName || ''
    }
  }
})
</script>
